import { ArrowDown, ArrowUp, Trash2 } from "lucide-react"

import { type CargoElegible, type Paso, SIN_FUNCION, TIPOS } from "./tipos"

/** Un paso del proceso en la vista de lista: título, orden, tipo y cargo responsable. */
export function PasoDelProceso({
  paso,
  indice,
  total,
  cargos,
  onCambiar,
  onMover,
  onBorrar,
}: {
  paso: Paso
  indice: number
  total: number
  cargos: CargoElegible[]
  onCambiar: (cambios: Partial<Paso>) => void
  onMover: (salto: number) => void
  onBorrar: () => void
}) {
  const numero = indice + 1
  return (
    <li
      data-testid={`paso-${paso.id}`}
      className="superficie p-3"
      style={{
        // Lo que no es función de nadie se atenúa: así se ve de un vistazo
        // qué va a acabar en un descriptivo y qué no.
        opacity: SIN_FUNCION.has(paso.tipo) ? 0.7 : 1,
      }}
    >
      <div className="flex items-start gap-2">
        <span
          className="mt-2 w-6 shrink-0 text-right text-xs tabular-nums"
          style={{ color: "var(--fg-muted)" }}
        >
          {numero}
        </span>
        <input
          className="campo flex-1"
          aria-label={`Título del paso ${numero}`}
          defaultValue={paso.titulo}
          onBlur={(e) => e.target.value !== paso.titulo && onCambiar({ titulo: e.target.value })}
        />
        <button
          type="button"
          onClick={() => onMover(-1)}
          disabled={indice === 0}
          aria-label={`Subir el paso ${numero}`}
          className="boton-secundario h-9 w-9 justify-center p-0"
        >
          <ArrowUp size={14} aria-hidden={true} />
        </button>
        <button
          type="button"
          onClick={() => onMover(1)}
          disabled={indice === total - 1}
          aria-label={`Bajar el paso ${numero}`}
          className="boton-secundario h-9 w-9 justify-center p-0"
        >
          <ArrowDown size={14} aria-hidden={true} />
        </button>
        <button
          type="button"
          onClick={onBorrar}
          aria-label={`Borrar el paso ${numero}`}
          className="boton-secundario h-9 w-9 justify-center p-0"
          style={{ color: "var(--destructive)" }}
        >
          <Trash2 size={14} aria-hidden={true} />
        </button>
      </div>

      <div className="mt-2 ml-8 grid gap-2 sm:grid-cols-2">
        <label className="text-xs" htmlFor={`tipo-${paso.id}`}>
          Tipo
          <select
            id={`tipo-${paso.id}`}
            className="campo mt-1"
            value={paso.tipo}
            onChange={(e) => onCambiar({ tipo: e.target.value as Paso["tipo"] })}
          >
            {TIPOS.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs" htmlFor={`resp-${paso.id}`}>
          Cargo responsable
          <select
            id={`resp-${paso.id}`}
            className="campo mt-1"
            data-testid={`resp-${paso.id}`}
            value={paso.responsable_cargo_id ?? ""}
            onChange={(e) =>
              onCambiar({
                responsable_cargo_id: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          >
            <option value="">Sin asignar</option>
            {cargos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
      </div>

      {SIN_FUNCION.has(paso.tipo) && (
        <p className="mt-2 ml-8 text-xs" style={{ color: "var(--fg-muted)" }}>
          Marca dónde empieza o acaba el proceso: no cuenta como función de nadie, así que no sale
          en ningún descriptivo.
        </p>
      )}
    </li>
  )
}
